import { isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'
import { NavLink, withRouter } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Avatar,
  Badge,
  Card,
  Divider,
  Dropdown,
  Layout,
  List,
  Menu,
  message,
  Modal,
  Space,
  Spin,
  Tooltip,
} from 'antd'
import { Button, DropdownMenu, ThemeToggle, Typography } from 'modules/core/components'
import { generateMessageError, getOperationsByKeys, isUserAllowed } from 'modules/core/utils'
import {
  DesktopOutlined,
  BellOutlined,
  FormatPainterOutlined,
  CheckCircleOutlined,
  CommentOutlined,
  SettingOutlined,
  StarOutlined,
  UserOutlined,
  GlobalOutlined,
  QuestionCircleOutlined,
  LogoutOutlined,
  DownOutlined,
} from '@ant-design/icons'
import { ROLES } from 'modules/core/constants'
import { connect } from 'react-redux'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import configuration from 'modules/configuration'
import planning from 'modules/planning'
import moment from 'moment'
import logoBlack from 'assets/images/logo/logo-black.svg'
import logoWhite from 'assets/images/logo/logo-white.svg'
import logoCompleto from 'assets/images/logo/logo.svg'

import './AppLayout.scss'
import BookmarkModal from '../SiderLayout/components/Bookmark/BookmarkModal'
import PropTypes from 'prop-types'

const { Header } = Layout
const { SETTINGS, PLANNING, CONTROL, FORECAST, ANALYSIS } = ROLES

const AppLayout = ({
  children,
  history,
  onLogout,
  loggedUser,
  showIcons = true,
  fetchDimensionsList,
  dimensionsList,
  periodList,
  fetchNotificationsList,
  notificationsList,
  markReaded,
  setShowAllComments,
  showAllComments,
  setHasComment,
  fetchOnlineUsers,
  onlineUsers,
  listBookmarks,
  bookmarks,
  openBookmark,
  setOpenBookmark,
}) => {
  const enableOnlineUsers = useFeatureIsOn('ab_applayout_onlineUsers')
  const abMonitor = useFeatureIsOn('ab-monitor')
  const enableThemeToggle = useFeatureIsOn('ab-theme-toggle')
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(false)
  const [value, setValue] = useState(600000)
  const [theme, setTheme] = useState(loggedUser?.theme || 'LIGHT')
  const [showBookmark, setShowBookmark] = useState(false)
  const [bookmark, setBookmark] = useState(null)
  const [openDropdown, setOpenDropdown] = useState(false)
  const { t } = useTranslation()

  const PLIKA_URL = 'https://drive.google.com/file/d/18hVE1eti4lTfMImSo4-60iaTxPDSow3p/view'

  useEffect(() => {
    const actualizarTema = () => {
      const newTheme = theme === 'LIGHT' ? 'DARK' : 'LIGHT'
      setTheme(newTheme || 'LIGHT')
    }

    // Escucha el evento personalizado
    window.addEventListener('theme-changed', actualizarTema)

    return () => {
      window.removeEventListener('theme-changed', actualizarTema)
    }
  }, [theme])

  useEffect(() => {
    if (enableOnlineUsers) {
      fetchOnlineUsers().then(() => setValue(600000))
    }
  }, [fetchOnlineUsers, value, enableOnlineUsers])

  useEffect(() => {
    if (enableOnlineUsers) {
      setTimeout(() => {
        setValue(0)
      }, value)
    }
  }, [value, enableOnlineUsers])

  useEffect(() => {
    setIsLoading(true)
    fetchNotificationsList({ page }).then((response) => {
      setIsLoading(false)
    })
  }, [fetchNotificationsList, page])

  useEffect(() => {
    fetchDimensionsList()
  }, [fetchDimensionsList])

  const exit = () =>
    onLogout().then(() => {
      history.push('/login')
    })

  const pathLogoComplete = ['/']
  const pathname = window.location.pathname

  const onClickBookmark = () => {
    const item = bookmarks.data.find((el) => el.url === window.location.href)
    setShowBookmark(true)
    setBookmark(item)
  }

  const headerMenus = [
    {
      title: t('FIELD_NOTIFICATIONS'),
      key: 'notification',
      icon: <BellOutlined />,
      allowedFor: [SETTINGS, PLANNING, CONTROL, FORECAST, ANALYSIS],
    },
    {
      title: t('LABEL_COMMENTS'),
      key: 'comments',
      icon: <CommentOutlined style={{ color: showAllComments && '#0047ba' }} />,
      allowedFor: [SETTINGS, PLANNING, CONTROL, FORECAST, ANALYSIS],
      onClick: () => setShowAllComments(!showAllComments),
    },
    {
      ...(abMonitor
        ? {
            title: 'Monitors',
            key: 'monitors',
            url: '/monitors',
            exact: false,
            icon: <DesktopOutlined />,
            allowedFor: [SETTINGS, PLANNING, CONTROL, FORECAST, ANALYSIS],
          }
        : {}),
    },
    {
      title: t('LABEL_BOOKMARKS'),
      key: 'bookmarks',
      icon: <StarOutlined />,
      allowedFor: [SETTINGS, PLANNING, CONTROL, FORECAST, ANALYSIS],
      onClick: () => onClickBookmark(),
    },
  ]

  // useEffect(() => {
  //   GA.pageView();
  // });

  const renderLogoBlack = () => (
    <div className="app-brand-logo" onClick={() => history.push('/')}>
      {enableThemeToggle && theme === 'DARK' ? (
        <img alt="brand-logo" width={20} src={logoWhite} />
      ) : (
        <img alt="brand-logo" src={logoBlack} />
      )}
    </div>
  )

  const renderLogoComplete = () => (
    <div className="app-brand-logo-complete" onClick={() => history.push('/')}>
      <img alt="brand-logo" src={logoCompleto} />
    </div>
  )

  const notificationType = (type) => {
    switch (type) {
      case 'comment':
        return CommentOutlined
      case 'mention':
      case 'share':
        return CheckCircleOutlined
      case 'send_notification':
        return BellOutlined
      default:
        return CheckCircleOutlined
    }
  }

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    if (scrollTop + clientHeight >= scrollHeight && notificationsList.next) {
      setPage((prevstate) => prevstate + 1)
    }
  }

  const handleClick = (notification) => {
    const { module, key, id: idNotification, type: typeNotify, url: urlNotify } = notification
    let urlRedirect = '/'
    let data = {}

    if (typeNotify === 'share') {
      urlRedirect = `/${urlNotify}`
    } else if (typeNotify === 'send_notification' && urlNotify) {
      urlRedirect = urlNotify
    } else {
      const phases = ['base', 'top_down', 'bottom_up', 'closing']
      const identifyData = key.split(module)[1]
      const type = identifyData.split('-')[1]
      const id = identifyData.split('-')[2]

      let includePhase = false
      let url = ''
      let phase = ''

      phases.forEach((el) => {
        if (module.includes(el)) {
          includePhase = true
          url = module.split(el)[0]
          phase = el
        }
      })

      data = {
        type,
        data: true,
        ...(type !== 'table' && {
          key: module,
          id,
        }),
      }

      urlRedirect = (includePhase ? `${url.slice(0, -1)}?phase=${phase}` : module).replaceAll(
        '-',
        '/',
      )
    }

    markReaded({ notifications: [idNotification] })
      .then(() => {
        setPage(1)
        fetchNotificationsList({ page }).then(() => {
          if (!isEmpty(data)) {
            setHasComment(data)
          }
          history.push(urlRedirect)
        })
      })
      .catch((error) =>
        message.error(generateMessageError(error, 'FEEDBACK_SAVE_CHANGES_FAIL_MESSAGE'), 8),
      )
  }

  const renderButtons = (url, exact, title, index, icon, item) => {
    if (url) {
      return (
        <NavLink
          exact={exact}
          to={url}
          className="header-icon"
          activeClassName="selected"
          title={title}
          key={index}
        >
          {icon}
        </NavLink>
      )
    } else if (item.key === 'bookmarks') {
      return (
        <DropdownMenu
          icon={icon}
          title={t('LABEL_BOOKMARKS')}
          menu={[
            {
              title: t('ACTION_VIEW_BOOKMARKS'),
              onClick: () => setOpenBookmark(!openBookmark),
            },
            {
              title: t('ACTION_CREATE_BOOKMARK'),
              onClick: () => item.onClick(),
            },
          ]}
        />
      )
    } else if (item.key === 'notification') {
      return (
        <Dropdown
          trigger={['click']}
          overlay={
            <div style={{ padding: 0 }}>
              <Card
                style={{ width: 400, overflow: 'hidden' }}
                bodyStyle={notificationsList.values.length > 0 ? { padding: 0 } : {}}
                headStyle={{ height: 56 }}
              >
                {notificationsList.values.length > 0 ? (
                  <>
                    <div style={{ overflowY: 'auto', maxHeight: 300 }} onScroll={handleScroll}>
                      <List
                        dataSource={notificationsList.values}
                        renderItem={(notification) => (
                          <List.Item
                            style={{
                              ...(!notification.is_read && { backgroundColor: '#e1effa' }),
                              cursor: 'pointer',
                            }}
                            onClick={() => handleClick(notification)}
                            key={notification.id}
                          >
                            <List.Item.Meta
                              style={{ padding: 5 }}
                              avatar={
                                <Space direction="horizontal">
                                  {!notification.is_read ? (
                                    <div
                                      style={{
                                        backgroundColor: '#0047ba',
                                        borderRadius: '50%',
                                        width: 5,
                                        height: 5,
                                      }}
                                    ></div>
                                  ) : (
                                    <div style={{ paddingLeft: 5 }}></div>
                                  )}
                                  <Avatar style={{ backgroundColor: '#0047ba' }}>
                                    {typeof notification.actor === 'object'
                                      ? notification.actor.first_name[0]
                                      : notification.actor[0]}
                                  </Avatar>
                                </Space>
                              }
                              title={
                                <Space direction="horizontal">
                                  <Typography.Body>
                                    {typeof notification.actor === 'object'
                                      ? notification.actor.full_name
                                      : notification.actor}
                                  </Typography.Body>
                                  <Typography.Body level={3}>
                                    {moment(notification.created_at).calendar()}
                                  </Typography.Body>
                                </Space>
                              }
                              description={
                                <Space
                                  direction="horizontal"
                                  style={{ display: 'flex', alignItems: 'flex-start' }}
                                >
                                  <Typography.Icon
                                    level={2}
                                    icon={notificationType(notification.type)}
                                  />
                                  {notification.streamline}
                                </Space>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    </div>
                    {isLoading && (
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Spin spinning={isLoading} />
                      </div>
                    )}
                  </>
                ) : (
                  <Typography.Body level={2}>{t('NO_NOTIFICATIONS_MESSAGE')}</Typography.Body>
                )}
              </Card>
            </div>
          }
        >
          <Badge size="small" count={notificationsList.unread_count}>
            <Button.Icon title={item.title} icon={item.icon} />
          </Badge>
        </Dropdown>
      )
    } else {
      return <Button.Icon key={index} title={title} icon={icon} onClick={item.onClick} />
    }
  }

  const renderTab = () => {
    const path = {
      planificacion: 'planning',
      seguimiento: 'control',
      analisis: 'analysis',
      forecast: 'forecast',
      configuracion: 'config',
    }
    let tab = ''

    Object.keys(path).forEach((el) => {
      if (window.location.pathname.includes(el)) {
        tab = path[el]
      }
    })

    return tab
  }

  const menu = [
    {
      title: t('PROFILE'),
      icon: <UserOutlined />,
      onClick: () => history.push(`/configuracion/usuarios/${loggedUser?.pkid}/perfil`),
    },
    {
      title: t('LABEL_CONFIG'),
      icon: <SettingOutlined />,
      onClick: () => history.push('/configuracion'),
    },
    {
      title: t('ACTION_DEFINE_THEME'),
      icon: <FormatPainterOutlined />,
      extraContent: enableThemeToggle && <ThemeToggle />,
      onClick: () => {},
    },
    {
      title: t('ACTION_DEFINE_LANGUAGE'),
      icon: <GlobalOutlined />,
      onClick: () => history.push(`/configuracion/usuarios/${loggedUser?.pkid}/preferences`),
    },
    {
      title: t('LABEL_HELP'),
      icon: <QuestionCircleOutlined />,
      submenu: [
        {
          title: t('LABEL_PLIKA_VIDEOS'),
          onClick: () => history.push(`/videosplika${renderTab() ? `?tab=${renderTab()}` : ''}`),
        },
        {
          title: t('LABEL_USER_MANUAL'),
          onClick: () => window.open(PLIKA_URL, '_blank', 'noopener'),
        },
      ],
      onClick: () => {},
    },
    {
      title: t('SIGN_OFF'),
      icon: <LogoutOutlined />,
      onClick: () => setShowLogoutModal(true),
    },
  ]

  return (
    <Layout className="app-layout">
      <Header className="app-layout-header">
        {pathLogoComplete.includes(pathname)
          ? dimensionsList.count === 0
            ? renderLogoComplete()
            : renderLogoBlack()
          : renderLogoBlack()}
        {showIcons && (
          <Space className="app-layout-actions" align="center" size="small">
            <Avatar.Group
              maxCount={4}
              maxStyle={{
                color: '#0047ba',
                backgroundColor: '#e1effa',
              }}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              {enableOnlineUsers &&
                onlineUsers
                  .filter((el) => el.pkid !== loggedUser?.pkid)
                  .map((el) => (
                    <Tooltip key={el.pkid} title={el.full_name}>
                      <Avatar
                        size="default"
                        key={el.pkid}
                        src={el.image}
                        style={{
                          backgroundColor: el.color,
                        }}
                      >
                        {`${el?.last_name[0].toUpperCase()}${el?.first_name[0].toUpperCase()}`}
                      </Avatar>
                    </Tooltip>
                  ))}
            </Avatar.Group>
            <Divider type="vertical" />
            {headerMenus
              .filter((navItem) => isUserAllowed(getOperationsByKeys(navItem.allowedFor)))
              .map((item, index) => {
                const { url, title, icon, exact } = item
                return renderButtons(url, exact, title, index, icon, item)
              })}
            <Avatar
              size="default"
              src={
                onlineUsers.length > 0
                  ? onlineUsers.filter((pp) => pp.pkid === loggedUser?.pkid)[0]?.image
                  : undefined
              }
              style={
                onlineUsers.length > 0
                  ? {
                      backgroundColor: onlineUsers.filter((pp) => pp.pkid === loggedUser?.pkid)[0]
                        ?.color,
                    }
                  : { backgroundColor: '#0047ba' }
              }
            >
              {`${loggedUser?.lastName[0].toUpperCase()}${loggedUser?.firstName[0].toUpperCase()}`}
            </Avatar>
            <Dropdown
              visible={openDropdown}
              onVisibleChange={(val) => setOpenDropdown(val)}
              overlay={
                <Menu
                  onClick={(e) => {
                    if (e.key === 'Definir tema_2') {
                      setOpenDropdown(true)
                    }
                  }}
                >
                  {menu.map((option, index) => {
                    if (option.submenu) {
                      return (
                        <Menu.SubMenu
                          key={`${option?.submenu[index]?.title}_${index}`}
                          title={option.title}
                          icon={option.icon}
                        >
                          {option.submenu.map((submenuItem) => (
                            <Menu.Item {...submenuItem}>{submenuItem.title}</Menu.Item>
                          ))}
                        </Menu.SubMenu>
                      )
                    }
                    return (
                      <Menu.Item
                        key={`${option.title}_${index}`}
                        icon={option.icon}
                        onClick={() => option?.onClick()}
                      >
                        <Space direction="horizontal">
                          {option.title}
                          {option.extraContent ? option.extraContent : ''}
                        </Space>
                      </Menu.Item>
                    )
                  })}
                </Menu>
              }
            >
              <a
                style={{ color: 'inherit' }}
                role="button"
                tabIndex="0"
                onClick={(e) => e.preventDefault()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                  }
                }}
              >
                <Space>
                  {loggedUser?.firstName && loggedUser?.lastName
                    ? `${loggedUser.firstName} ${loggedUser.lastName}`
                    : loggedUser?.email || 'Plika User'}
                  <DownOutlined />
                </Space>
              </a>
            </Dropdown>
          </Space>
        )}
      </Header>
      <Layout className="app-layout-body">{children}</Layout>
      <Modal
        visible={showLogoutModal}
        onCancel={() => setShowLogoutModal(false)}
        onOk={exit}
        cancelText={t('ACTION_CANCEL')}
        okText={t('SIGN_OFF')}
        title={t('SIGN_OFF')}
      >
        {t('SIGN_OFF_CONFIRM_MESSAGE')}
      </Modal>
      <BookmarkModal
        bookmark={bookmark}
        showBookmark={showBookmark}
        setShowBookmark={setShowBookmark}
      />
    </Layout>
  )
}

const mapStateToProps = (state) => ({
  dimensionsList: configuration.selectors.getDimensionsList(state),
  periodList: planning.selectors.getPeriodList(state),
  notificationsList: configuration.selectors.getNotificationsList(state),
  showAllComments: configuration.selectors.getShowAllComments(state),
  onlineUsers: configuration.selectors.getOnlineUsers(state),
  bookmarks: configuration.selectors.getBookmarks(state),
  openBookmark: configuration.selectors.getOpenBookmarks(state),
})

const mapDispatchToProps = {
  fetchDimensionsList: configuration.actions.fetchDimensionsList,
  fetchNotificationsList: configuration.actions.fetchNotificationsList,
  markReaded: configuration.actions.markReaded,
  setShowAllComments: configuration.actions.setShowAllComments,
  setHasComment: configuration.actions.setHasComment,
  fetchOnlineUsers: configuration.actions.fetchOnlineUsers,
  setOpenBookmark: configuration.actions.setOpenBookmark,
}

AppLayout.propTypes = {
  fetchNotificationsList: PropTypes.func,
  notificationsList: PropTypes.shape({
    next: PropTypes.any,
    values: PropTypes.array,
    unread_count: PropTypes.number,
  }),
  markReaded: PropTypes.func,
  setShowAllComments: PropTypes.func,
  showAllComments: PropTypes.bool,
  setHasComment: PropTypes.func,
  fetchOnlineUsers: PropTypes.func,
  onlineUsers: PropTypes.array,
  listBookmarks: PropTypes.any,
  bookmarks: PropTypes.shape({
    data: PropTypes.array,
  }),
  openBookmark: PropTypes.bool,
  setOpenBookmark: PropTypes.func,
  loggedUser: PropTypes.shape({
    theme: PropTypes.string,
    link_upload_file: PropTypes.any,
    pkid: PropTypes.any,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
  }),
  history: PropTypes.any,
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AppLayout))
